import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Search, Download, UserPlus, Calendar, X, MessageSquare, Eye } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaAsignaturasMatriculacion() {
    const [asignaturas, setAsignaturas] = useState([]);
    const [asignaciones, setAsignaciones] = useState([]);
    const [carreras, setCarreras] = useState([]);
    const [menciones, setMenciones] = useState([]);
    const [docentes, setDocentes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Filtros
    const [filtroGestion, setFiltroGestion] = useState(new Date().getFullYear());
    const [filtroCarrera, setFiltroCarrera] = useState('');
    const [filtroMencion, setFiltroMencion] = useState('');
    const [filtroNivel, setFiltroNivel] = useState('');

    // Modales
    const [showModal, setShowModal] = useState(false);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [filtroSala, setFiltroSala] = useState('');  // Filter for sala in calendar view
    const [filtroDocente, setFiltroDocente] = useState('');
    const [filtroDia, setFiltroDia] = useState('');
    const [selectedObservation, setSelectedObservation] = useState(null);

    const [selectedAsignatura, setSelectedAsignatura] = useState(null);
    const [formData, setFormData] = useState({
        id_docente: '',
        paralelo: '',
        capacidad: 30,
        dias: [],
        color: '#3B82F6',
        horarios_detalle: {}
    });

    // LOGICA EXCEL DESCARGADO //

    const getSlotRango = (horaInicio) => {
        const [h, m] = horaInicio.split(':').map(Number);
        const nextM = m === 30 ? 0 : 30;
        const nextH = m === 30 ? h + 1 : h;
        const end = `${nextH.toString().padStart(2, '0')}:${nextM.toString().padStart(2, '0')}`;
        return `${horaInicio} - ${end}`;
    };

    const handleDownloadExcel = () => {
        try {
            const rows = [];
            let rowCounter = 1;

            if (!asignaturasFiltradas || asignaturasFiltradas.length === 0) {
                alert('No hay datos para exportar');
                return;
            }

            asignaturasFiltradas.forEach((a) => {
                const paralelos = getAsignaciones(a.id_asignatura, a.id_carrera, a.id_mencion);

                if (paralelos.length === 0) {
                    rows.push({
                        'No': rowCounter++,
                        'Año': `${a.nivel}° Año`,
                        'Sigla': a.codigo || '-',
                        'Asignatura': a.nombre || '-',
                        'Carrera': getCarreraNombre(a.id_carrera),
                        'Mencion': getMencionNombre(a.id_mencion),
                        'Docente': '-',
                        'Sala': '-',
                        'Paralelo': '-',
                        'Capacidad': '-',
                        '% Capacidad': '0/30 (0%)',
                        'Horario': '-',
                        'Días': '-',
                        'Estado': 'Sin asignar',
                        'Observaciones': '-'
                    });
                } else {
                    paralelos.forEach((p) => {
                        const det = p.horarios_detalle && Array.isArray(p.horarios_detalle) ? p.horarios_detalle : null;

                        // Calcular capacidad
                        const cap = parseInt(p.capacidad) || 30;
                        const ins = parseInt(p.inscritos) || 0;
                        const porc = Math.min(100, Math.round((ins / cap) * 100));

                        rows.push({
                            'No': rowCounter++,
                            'Año': `${a.nivel}° Año`,
                            'Sigla': a.codigo || '-',
                            'Asignatura': a.nombre || '-',
                            'Carrera': getCarreraNombre(a.id_carrera),
                            'Mencion': getMencionNombre(a.id_mencion),
                            'Docente': getDocenteNombre(p.id_docente) || '-',
                            'Sala': det ? det.map(d => d.sala).join(', ') : (p.sala || '-'),
                            'Paralelo': p.paralelo || '-',
                            'Capacidad': cap,
                            '% Capacidad': `${ins}/${cap} (${porc}%)`,
                            'Horario': det
                                ? det.map(d => `${d.hora_inicio?.substring(0, 5)} - ${d.hora_fin?.substring(0, 5)}`).join(' | ')
                                : `${p.hora_inicio?.substring(0, 5)} - ${p.hora_fin?.substring(0, 5)}`,
                            'Días': det
                                ? det.map(d => d.dia).join(', ')
                                : (Array.isArray(p.dias) ? p.dias.join(', ') : (p.dias || '-')),
                            'Estado': 'Asignado',
                            'Observaciones': p.observaciones || '-'
                        });
                    });
                }
            });

            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Horarios_Matriculacion');
            XLSX.writeFile(wb, `Reporte_Horarios_Licenciatura_${filtroGestion}.xlsx`);
        } catch (error) {
            console.error('Error al descargar Excel:', error);
            alert('Error al generar el archivo Excel.');
        }
    };

    const gestiones = [2023, 2024, 2025, 2026, 2027, 2028];
    const niveles = [
        { value: 1, label: 'Primer Año' },
        { value: 2, label: 'Segundo Año' },
        { value: 3, label: 'Tercer Año' },
        { value: 4, label: 'Cuarto Año' }
    ];
    const diasSemana = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
    const horasDisponibles = [];
    for (let h = 6; h <= 18; h++) {
        horasDisponibles.push(`${h.toString().padStart(2, '0')}:00`);
        horasDisponibles.push(`${h.toString().padStart(2, '0')}:30`);
    }


    useEffect(() => {
        loadData();
    }, [filtroGestion, filtroCarrera, filtroMencion, filtroNivel]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Cargar asignaturas, carreras, menciones, docentes
            const params = new URLSearchParams();
            params.append('gestion', filtroGestion);
            if (filtroCarrera) params.append('id_carrera', filtroCarrera);
            if (filtroMencion) params.append('id_mencion', filtroMencion);
            if (filtroNivel) params.append('nivel', filtroNivel);

            const [asignaturasRes, carrerasRes, mencionesRes, docentesRes, asignacionesRes] = await Promise.all([
                fetch(`${API_URL}/jefatura/asignaturas?${params.toString()}`),
                fetch(`${API_URL}/jefatura/carreras`),
                fetch(`${API_URL}/jefatura/menciones`),
                fetch(`${API_URL}/jefatura/docentes`),
                fetch(`${API_URL}/jefatura/asignaciones-horario?gestion=${filtroGestion}`)
            ]);

            const asignaturasData = await asignaturasRes.json();
            const carrerasData = await carrerasRes.json();
            const mencionesData = await mencionesRes.json();
            const docentesData = await docentesRes.json();
            const asignacionesData = await asignacionesRes.json();

            setAsignaturas(asignaturasData.asignaturas || []);
            setCarreras(carrerasData.carreras || []);
            setMenciones(mencionesData.menciones || []);
            setDocentes(docentesData.docentes || []);
            setAsignaciones(asignacionesData.asignaciones || []);
        } catch (error) {
            console.error('Error cargando datos:', error);
        } finally {
            setLoading(false);
        }
    };

    // Obtener la asignación para una asignatura (si existe) - primera match
    const getAsignacion = (idAsignatura) => {
        return asignaciones.find(a =>
            parseInt(a.id_asignatura) === parseInt(idAsignatura) &&
            parseInt(a.gestion) === parseInt(filtroGestion)
        );
    };

    // Obtener TODAS las asignaciones/paralelos para una asignatura (filtrado por carrera/mencion)
    const getAsignaciones = (idAsignatura, idCarrera, idMencion) => {
        return asignaciones.filter(a => {
            if (parseInt(a.id_asignatura) !== parseInt(idAsignatura)) return false;
            if (parseInt(a.gestion) !== parseInt(filtroGestion)) return false;

            // Fix: No filtrar por carrera/mención estrictamente. 
            // Si coincide el ID de asignaturay gestión, es válido.
            return true;
        });
    };

    const handleAsignarDocente = (asignatura) => {
        setSelectedAsignatura(asignatura);
        setFormData({
            id_docente: '',
            paralelo: '',
            capacidad: 30,
            dias: [],
            color: '#3B82F6',
            horarios_detalle: {},
            id_asignacion: null
        });
        setShowModal(true);
    };

    const handleEditarParalelo = (asignatura, asignacion) => {
        setSelectedAsignatura(asignatura);

        // Cargar horarios_detalle si existe, sino migrar de campos legacy
        let horariosDetalle = {};
        if (asignacion.horarios_detalle && Array.isArray(asignacion.horarios_detalle) && asignacion.horarios_detalle.length > 0) {
            asignacion.horarios_detalle.forEach(d => {
                horariosDetalle[d.dia] = {
                    hora_inicio: d.hora_inicio || '',
                    hora_fin: d.hora_fin || '',
                    sala: d.sala || ''
                };
            });
        } else if (asignacion.dias && asignacion.dias.length > 0) {
            asignacion.dias.forEach(dia => {
                horariosDetalle[dia] = {
                    hora_inicio: asignacion.hora_inicio?.substring(0, 5) || '',
                    hora_fin: asignacion.hora_fin?.substring(0, 5) || '',
                    sala: asignacion.sala || ''
                };
            });
        }

        setFormData({
            id_docente: asignacion.id_docente || '',
            paralelo: asignacion.paralelo || '',
            capacidad: asignacion.capacidad || 30,
            dias: asignacion.dias || [],
            color: asignacion.color || '#3B82F6',
            horarios_detalle: horariosDetalle,
            id_asignacion: asignacion.id_asignacion
        });
        setShowModal(true);
    };

    const handleGuardarAsignacion = async () => {
        if (!formData.id_docente) {
            alert('Debe seleccionar un docente');
            return;
        }
        if (!formData.paralelo || formData.paralelo.length < 1 || formData.paralelo.length > 2) {
            alert('El paralelo debe tener 1 o 2 caracteres (ej: A, A1, B2)');
            return;
        }
        if (formData.dias.length === 0) {
            alert('Debe seleccionar al menos un día');
            return;
        }
        // Validar que cada día tenga hora y sala
        for (const dia of formData.dias) {
            const det = formData.horarios_detalle[dia];
            if (!det?.hora_inicio || !det?.hora_fin || !det?.sala) {
                alert(`Complete hora inicio, hora fin y sala para ${dia}`);
                return;
            }
        }

        try {
            const isEdit = !!formData.id_asignacion;
            const url = isEdit
                ? `${API_URL}/jefatura/asignaciones-horario/${formData.id_asignacion}`
                : `${API_URL}/jefatura/asignaciones-horario`;

            // Construir horarios_detalle array
            const horariosDetalle = formData.dias.map(dia => ({
                dia,
                hora_inicio: formData.horarios_detalle[dia]?.hora_inicio || '',
                hora_fin: formData.horarios_detalle[dia]?.hora_fin || '',
                sala: formData.horarios_detalle[dia]?.sala || ''
            }));

            const res = await fetch(url, {
                method: isEdit ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_asignatura: parseInt(selectedAsignatura.id_asignatura),
                    gestion: parseInt(filtroGestion),
                    id_docente: parseInt(formData.id_docente),
                    sala: horariosDetalle[0]?.sala || '',
                    paralelo: formData.paralelo.toUpperCase(),
                    capacidad: formData.capacidad,
                    hora_inicio: horariosDetalle[0]?.hora_inicio || '',
                    hora_fin: horariosDetalle[0]?.hora_fin || '',
                    dias: formData.dias,
                    color: formData.color,
                    id_carrera: selectedAsignatura.id_carrera || null,
                    id_mencion: selectedAsignatura.id_mencion || null,
                    horarios_detalle: horariosDetalle
                })
            });

            const data = await res.json();
            if (res.ok) {
                if (data.warning) {
                    alert(data.warning);
                }
                setShowModal(false);
                loadData();
            } else {
                alert('Error: ' + data.message);
            }
        } catch (error) {
            console.error('Error guardando:', error);
            alert('Error al guardar la asignación');
        }
    };

    // Filtrar menciones por carrera
    const getMencionesFiltradas = () => {
        if (!filtroCarrera) return [];
        return menciones.filter(m => m.id_carrera === parseInt(filtroCarrera));
    };

    // Búscalas arriba y déjalas exactamente así (solo una vez en todo el archivo):
    const getCarreraNombre = (id) => {
        if (!id) return '—';
        return carreras.find(c => parseInt(c.id_carrera) === parseInt(id))?.nombre || '—';
    };

    const getMencionNombre = (id) => {
        if (!id) return '—';
        return menciones.find(m => parseInt(m.id_mencion) === parseInt(id))?.nombre || '—';
    };
    // Obtener nombre de docente
    const getDocenteNombre = (id) => {
        if (!id || !docentes) return '-';
        const d = docentes.find(doc => doc && parseInt(doc.id_docente) === parseInt(id));
        return (d && d.nombres) ? `${d.nombres} ${d.apellidos}` : '-';
    };

    // Obtener nivel como texto
    const getNivelTexto = (nivel) => {
        const n = niveles.find(l => l.value === nivel);
        return n ? n.label : `Año ${nivel}`;
    };

    // Filtrar asignaturas por búsqueda (Nombre, Código, Docente, Sala, Paralelo, Año)

    const asignaturasFiltradas = (() => {
        // 1. Si no hay búsqueda, devolver TODAS las asignaturas (pero deduplicadas)
        if (!searchTerm || searchTerm.trim() === '') {
            const unique = new Map();
            asignaturas.forEach(item => {
                if (!unique.has(item.id_asignatura)) {
                    unique.set(item.id_asignatura, item);
                }
            });
            return Array.from(unique.values());
        }

        const search = searchTerm.toLowerCase().trim();

        // 2. Filtrar
        const filtered = asignaturas.filter(a => {
            // Matches asignatura info
            const codigo = (a.codigo || '').toLowerCase();
            const nombre = (a.nombre || '').toLowerCase();
            const nivel = getNivelTexto(a.nivel).toLowerCase();

            if (codigo.includes(search) ||
                nombre.includes(search) ||
                nivel.includes(search)) {
                return true;
            }

            // Matches assignments info
            const assignments = getAsignaciones(a.id_asignatura, a.id_carrera, a.id_mencion) || [];
            return assignments.some(asig => {
                const docenteName = getDocenteNombre(asig.id_docente).toLowerCase();
                const sala = (asig.sala || '').toLowerCase();
                const paralelo = (asig.paralelo || '').toLowerCase();

                return docenteName.includes(search) ||
                    sala.includes(search) ||
                    paralelo.includes(search);
            });
        });

        // 3. Deduplicar resultados filtrados
        const uniqueFiltered = new Map();
        filtered.forEach(item => {
            if (!uniqueFiltered.has(item.id_asignatura)) {
                uniqueFiltered.set(item.id_asignatura, item);
            }
        });
        return Array.from(uniqueFiltered.values());
    })();



    const toggleDia = (dia) => {
        setFormData(prev => {
            const newDias = prev.dias.includes(dia)
                ? prev.dias.filter(d => d !== dia)
                : [...prev.dias, dia];
            const newDetalle = { ...prev.horarios_detalle };
            if (!prev.dias.includes(dia)) {
                // Añadiendo día
                newDetalle[dia] = newDetalle[dia] || { hora_inicio: '', hora_fin: '', sala: '' };
            } else {
                // Quitando día
                delete newDetalle[dia];
            }
            return { ...prev, dias: newDias, horarios_detalle: newDetalle };
        });
    };

    const handleDetalleChange = (dia, field, value) => {
        setFormData(prev => ({
            ...prev,
            horarios_detalle: {
                ...prev.horarios_detalle,
                [dia]: {
                    ...prev.horarios_detalle[dia],
                    [field]: value
                }
            }
        }));
    };


    const [showNewAsigModal, setShowNewAsigModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [estadoGestion, setEstadoGestion] = useState(null);
    const [editingSubject, setEditingSubject] = useState(null);
    const [newAsigData, setNewAsigData] = useState({
        codigo: '',
        nombre: '',
        horas: 4,
        id_carrera: '',
        id_mencion: '',
        nivel: 1,
        prerequisito_codigo: ''
    });

    // Abrir modal para editar asignatura
    const openEditSubjectModal = (asig) => {
        setEditingSubject(asig);
        setNewAsigData({
            codigo: asig.codigo || '',
            nombre: asig.nombre || '',
            horas: asig.horas || 4,
            id_carrera: asig.id_carrera || '',
            id_mencion: asig.id_mencion || '',
            nivel: asig.nivel || 1,
            prerequisito_codigo: asig.prerequisito_codigo || ''
        });
        setShowNewAsigModal(true);
    };

    // Abrir modal para crear nueva asignatura
    const openNewAsigModal = () => {
        setEditingSubject(null);
        setNewAsigData({
            codigo: '',
            nombre: '',
            horas: 4,
            id_carrera: filtroCarrera || '',
            id_mencion: filtroMencion || '',
            nivel: filtroNivel || 1,
            prerequisito_codigo: ''
        });
        setShowNewAsigModal(true);
    };

    const handleCreateAsignatura = async () => {
        if (!newAsigData.codigo || !newAsigData.nombre || !newAsigData.id_carrera || !newAsigData.id_mencion) {
            alert('Por favor complete todos los campos obligatorios');
            return;
        }

        try {
            let res;
            if (editingSubject) {
                // Editar
                res = await fetch(`${API_URL}/jefatura/asignaturas/${editingSubject.id_asignatura}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(newAsigData)
                });
            } else {
                // Crear
                res = await fetch(`${API_URL}/jefatura/asignaturas`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(newAsigData)
                });
            }

            if (res.ok) {
                alert(editingSubject ? 'Asignatura actualizada' : 'Asignatura creada exitosamente');
                setShowNewAsigModal(false);
                setEditingSubject(null);
                setNewAsigData({
                    codigo: '',
                    nombre: '',
                    horas: 4,
                    id_carrera: '',
                    id_mencion: '',
                    nivel: 1,
                    prerequisito_codigo: ''
                });
                loadData();
            } else {
                const error = await res.json();
                alert('Error: ' + (error.message || 'Error al guardar'));
            }
        } catch (error) {
            console.error('Error guardando asignatura:', error);
            alert('Error al guardar asignatura');
        }
    };

    // Helper para mostrar días en calendario
    const getDiasAbrev = (diasArr) => {
        if (!diasArr || diasArr.length === 0) return '-';
        return diasArr.map(d => {
            if (d === 'Lunes') return 'L';
            if (d === 'Martes') return 'M';
            if (d === 'Miércoles') return 'MI';
            if (d === 'Jueves') return 'J';
            if (d === 'Viernes') return 'V';
            return d;
        }).join(', ');
    };

    // ============================================================
    // FIX COLORES: en la BD, todas las asignaciones tienen guardado
    // el color por defecto del selector ("#3B82F6"), porque nadie lo
    // cambió manualmente. Antes el código hacía `a.color || hash`, y
    // como a.color SIEMPRE existía (aunque fuera el default), nunca
    // llegaba a generar un color distinto -> todo se veía igual.
    // Ahora: si el color guardado es el default, se genera un color
    // estable por hash (id_asignatura + paralelo); si el usuario
    // eligió otro color en el modal, ese se respeta siempre.
    // El cálculo se hace UNA sola vez sobre todas las asignaciones
    // (no en cada render del calendario), así el color de una clase
    // no cambia al mover los filtros de Sala/Docente/Día.
    // ============================================================
    const COLOR_POR_DEFECTO = '#3B82F6';

    const colorPorClase = useMemo(() => {
        const stringToColor = (str) => {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                hash = str.charCodeAt(i) + ((hash << 5) - hash);
            }
            const hue = Math.abs(hash % 360);
            return `hsl(${hue}, 70%, 50%)`;
        };
        const map = {};
        asignaciones.forEach(a => {
            const claseId = `${a.id_asignatura}-${a.paralelo || ''}`;
            if (!map[claseId]) {
                const tieneColorPropio = a.color && a.color.toUpperCase() !== COLOR_POR_DEFECTO.toUpperCase();
                map[claseId] = tieneColorPropio ? a.color : stringToColor(claseId);
            }
        });
        return map;
    }, [asignaciones]);

    if (loading) {
        return <div className="p-6 text-center">Cargando asignaturas...</div>;
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">
                    Jefatura de Carrera &gt; Asignaturas &gt; Licenciatura
                </h1>
            </div>

            {/* Toolbar y Filtros */}
            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end mb-4">
                    {/* Gestión */}
                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={filtroGestion}
                            onChange={e => setFiltroGestion(parseInt(e.target.value))}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {gestiones.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                        <span className="absolute right-3 top-[34px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>

                    {/* Carrera */}
                    <div className="relative flex-grow max-w-xs">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">CARRERA</label>
                        <select
                            value={filtroCarrera}
                            onChange={e => { setFiltroCarrera(e.target.value); setFiltroMencion(''); }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        >
                            <option value="">Todas las Carreras</option>
                            {carreras.filter(c => [8, 9, 10].includes(c.id_carrera)).map(c => (
                                <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>
                            ))}
                        </select>
                        <span className="absolute right-3 top-[34px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>

                    {/* Mención */}
                    <div className="relative flex-grow max-w-xs">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">MENCIÓN</label>
                        <select
                            value={filtroMencion}
                            onChange={e => setFiltroMencion(e.target.value)}
                            disabled={!filtroCarrera}
                            className={`w-full px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 ${!filtroCarrera ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                        >
                            <option value="">{filtroCarrera ? 'Todas las Menciones' : 'Seleccione una carrera primero'}</option>
                            {getMencionesFiltradas().map(m => (
                                <option key={m.id_mencion} value={m.id_mencion}>{m.nombre}</option>
                            ))}
                        </select>
                        <span className="absolute right-3 top-[34px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>

                    {/* Año */}
                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">NIVEL</label>
                        <select
                            value={filtroNivel}
                            onChange={e => setFiltroNivel(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[120px]"
                        >
                            <option value="">Todos</option>
                            {niveles.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
                        </select>
                        <span className="absolute right-3 top-[34px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>

                    {/* Buscar */}
                    <div className="relative flex-grow">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                            <input
                                type="text"
                                placeholder="Buscar: nombre, código, docente, sala, paralelo..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Botones Acciones */}
                <div className="flex flex-wrap gap-3 items-center">
                    <button
                        onClick={() => setShowCalendarModal(true)}
                        className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800"
                    >
                        <Calendar size={16} /> Ver Horario
                    </button>
                    <button
                        onClick={() => setShowNewAsigModal(true)}
                        className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-full hover:bg-green-700"
                    >
                        <Plus size={18} />
                        Añadir Asignatura
                    </button>
                    <button
                        onClick={() => setShowApprovalModal(true)}
                        className="flex items-center gap-2 px-6 py-2 bg-orange-500 text-white rounded-full hover:bg-orange-600"
                    >
                        Solicitar aprobación
                    </button>
                    <button
                        onClick={handleDownloadExcel}
                        className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                        title="Descargar Excel"
                    >
                        <Download size={20} />
                    </button>
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">No</th>
                                <th className="px-3 py-3 text-center font-semibold border">Año</th>
                                <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                                <th className="px-3 py-3 text-center font-semibold border">Carrera</th>
                                <th className="px-3 py-3 text-center font-semibold border">Mención</th>
                                <th className="px-3 py-3 text-center font-semibold border">Docente</th>
                                <th className="px-3 py-3 text-center font-semibold border">Sala</th>
                                <th className="px-3 py-3 text-center font-semibold border">Paralelo</th>
                                <th className="px-3 py-3 text-center font-semibold border">Capacidad</th>
                                <th className="px-3 py-3 text-center font-semibold border">% Capacidad</th>
                                <th className="px-3 py-3 text-center font-semibold border">Horario</th>
                                <th className="px-3 py-3 text-center font-semibold border">Días</th>
                                <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                <th className="px-3 py-3 text-center font-semibold border">Observaciones</th>
                                <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {asignaturasFiltradas.length === 0 ? (
                                <tr>
                                    <td colSpan={15} className="text-center py-12 text-gray-500">
                                        No hay asignaturas que coincidan con los filtros
                                    </td>
                                </tr>
                            ) : (
                                asignaturasFiltradas.map((a, idx) => {
                                    const paralelos = getAsignaciones(a.id_asignatura, a.id_carrera, a.id_mencion);
                                    const hasParalelos = paralelos.length > 0;

                                    // Si no tiene paralelos, mostrar una fila con opción de agregar
                                    if (!hasParalelos) {
                                        return (
                                            <tr key={a.id_asignatura} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 text-center border">{idx + 1}</td>
                                                <td className="px-3 py-2 text-center border text-gray-600 text-xs">{a.nivel}° Año</td>
                                                <td className="px-3 py-2 border font-medium">
                                                    <div className="font-semibold text-gray-800">{a.codigo}</div>
                                                    <div className="text-gray-600 text-xs">{a.nombre}</div>
                                                </td>
                                                <td className="px-3 py-2 border text-gray-700 text-xs font-medium">{getCarreraNombre(a.id_carrera)}</td>
                                                <td className="px-3 py-2 border text-gray-600 text-xs italic">{getMencionNombre(a.id_mencion)}</td>
                                                <td className="px-3 py-2 text-center border" colSpan={9}>
                                                    <span className="text-gray-400 italic text-xs">Sin paralelos asignados</span>
                                                </td>
                                                <td className="px-3 py-2 text-center border">
                                                    <div className="flex justify-center gap-1">
                                                        <button
                                                            onClick={() => openEditSubjectModal(a)}
                                                            className="p-1.5 rounded-full transition shadow-sm bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                                                            title="Editar asignatura"
                                                        >
                                                            <Edit size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleAsignarDocente(a)}
                                                            className="p-1.5 rounded-full transition shadow-sm bg-green-100 text-green-600 hover:bg-green-200"
                                                            title="Agregar paralelo"
                                                        >
                                                            <Plus size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }

                                    // Si tiene paralelos, mostrar una fila por cada paralelo
                                    return paralelos.map((asig, pIdx) => (
                                        <tr key={`${a.id_asignatura}-${asig.id_asignacion || pIdx}`} className={`hover:bg-gray-50 ${pIdx > 0 ? 'bg-gray-50/50' : ''}`}>
                                            {/* Solo mostrar número, año, asignatura, carrera y mención en la primera fila del grupo */}
                                            {pIdx === 0 ? (
                                                <>
                                                    <td className="px-3 py-2 text-center border" rowSpan={paralelos.length}>{idx + 1}</td>
                                                    <td className="px-3 py-2 text-center border text-gray-600 text-xs" rowSpan={paralelos.length}>{a.nivel}° Año</td>
                                                    <td className="px-3 py-2 border font-medium" rowSpan={paralelos.length}>
                                                        <div className="font-semibold text-gray-800">{a.codigo}</div>
                                                        <div className="text-gray-600 text-xs">{a.nombre}</div>
                                                    </td>
                                                    <td className="px-3 py-2 border text-gray-700 text-xs font-medium" rowSpan={paralelos.length}>
                                                        {getCarreraNombre(a.id_carrera)}
                                                    </td>
                                                    <td className="px-3 py-2 border text-gray-600 text-xs italic" rowSpan={paralelos.length}>
                                                        {getMencionNombre(a.id_mencion)}
                                                    </td>
                                                </>
                                            ) : null}
                                            <td className="px-3 py-2 text-center border">
                                                <span title={getDocenteNombre(asig.id_docente)}>
                                                    {asig.id_docente ? getDocenteNombre(asig.id_docente) : <span className="text-gray-400">-</span>}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-center border">
                                                {(() => {
                                                    const det = asig.horarios_detalle && Array.isArray(asig.horarios_detalle) ? asig.horarios_detalle : null;
                                                    return det ? det.map((d, i) => <div key={i}>{d.sala || '-'}</div>) : (asig?.sala || '-');
                                                })()}
                                            </td>
                                            <td className="px-3 py-2 text-center border">{asig?.paralelo || '-'}</td>
                                            <td className="px-3 py-2 text-center border">{asig?.capacidad || 30}</td>
                                            <td className="px-3 py-2 text-center border">
                                                {(() => {
                                                    const cap = parseInt(asig?.capacidad) || 30;
                                                    const ins = parseInt(asig?.inscritos) || 0;
                                                    const porc = Math.min(100, Math.round((ins / cap) * 100));
                                                    return (
                                                        <div className="flex flex-col items-center">
                                                            <span className={`font-bold ${porc >= 100 ? 'text-red-600' : porc > 80 ? 'text-orange-600' : 'text-green-600'}`}>
                                                                {ins}/{cap} ({porc}%)
                                                            </span>
                                                            {porc >= 100 && <span className="text-[10px] text-red-700 font-bold uppercase">Lleno</span>}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-3 py-2 text-center border text-xs whitespace-nowrap">
                                                {(() => {
                                                    const det = asig.horarios_detalle && Array.isArray(asig.horarios_detalle) ? asig.horarios_detalle : null;
                                                    if (det) {
                                                        return det.map((d, i) => (
                                                            <div key={i}>
                                                                <span >
                                                                    {d.hora_inicio?.substring(0, 5)} - {d.hora_fin?.substring(0, 5)}
                                                                </span>
                                                            </div>
                                                        ));
                                                    }
                                                    return (
                                                        <span className="bg-gray-100 px-2 py-1 rounded text-gray-700">
                                                            {asig.hora_inicio?.substring(0, 5)} - {asig.hora_fin?.substring(0, 5)}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-3 py-2 text-center border text-xs font-medium">
                                                {(() => {
                                                    const det = asig.horarios_detalle && Array.isArray(asig.horarios_detalle) ? asig.horarios_detalle : null;
                                                    if (det) {
                                                        return det.map((d, i) => (
                                                            <div key={i}>
                                                                <span>{d.dia?.substring(0, 2)}</span>
                                                            </div>
                                                        ));
                                                    }
                                                    return asig?.dias ? (
                                                        <span className="text-blue-600">{asig.dias.map(d => d.substring(0, 2)).join(', ')}</span>
                                                    ) : '-';
                                                })()}
                                            </td>
                                            <td className="px-3 py-2 text-center border">
                                                {(() => {
                                                    const estado = asig.estado_direccion || 'borrador';
                                                    const estilos = {
                                                        borrador: 'bg-gray-100 text-gray-600',
                                                        pendiente: 'bg-yellow-100 text-yellow-700',
                                                        aprobado: 'bg-green-100 text-green-700',
                                                        rechazado: 'bg-red-100 text-red-700'
                                                    };
                                                    const labels = {
                                                        borrador: 'Sin enviar',
                                                        pendiente: 'Pendiente',
                                                        aprobado: 'Aprobado',
                                                        rechazado: 'Rechazado'
                                                    };
                                                    return (
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${estilos[estado] || estilos.borrador}`}>
                                                            {labels[estado] || labels.borrador}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-3 py-2 text-center border">
                                                {asig.observaciones_direccion ? (
                                                    <button
                                                        onClick={() => setSelectedObservation(asig.observaciones_direccion)}
                                                        className="p-1.5 rounded-full transition shadow-sm bg-orange-100 text-orange-600 hover:bg-orange-200"
                                                        title="Ver observaciones"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-300 text-xs">—</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-center border">
                                                <div className="flex gap-1 justify-center">
                                                    <button
                                                        onClick={() => handleEditarParalelo(a, asig)}
                                                        className="p-1.5 rounded-full transition shadow-sm bg-blue-100 text-blue-600 hover:bg-blue-200"
                                                        title="Editar paralelo"
                                                    >
                                                        <Edit size={14} />
                                                    </button>
                                                    {pIdx === 0 && (
                                                        <>
                                                            <button
                                                                onClick={() => openEditSubjectModal(a)}
                                                                className="p-1.5 rounded-full transition shadow-sm bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                                                                title="Editar asignatura"
                                                            >
                                                                <Edit size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleAsignarDocente(a)}
                                                                className="p-1.5 rounded-full transition shadow-sm bg-green-100 text-green-600 hover:bg-green-200"
                                                                title="Agregar otro paralelo"
                                                            >
                                                                <Plus size={14} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ));
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Nueva/Editar Asignatura */}
            {showNewAsigModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
                        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
                            <h2 className="text-xl font-bold text-blue-900">
                                {editingSubject ? 'Editar Asignatura' : 'Adicionar Asignatura'}
                            </h2>
                            <button onClick={() => { setShowNewAsigModal(false); setEditingSubject(null); }} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Nombre Asignatura *</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 uppercase focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none"
                                        value={newAsigData.nombre}
                                        onChange={e => setNewAsigData({ ...newAsigData, nombre: e.target.value.toUpperCase() })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Código *</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 uppercase focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none"
                                        value={newAsigData.codigo}
                                        onChange={e => setNewAsigData({ ...newAsigData, codigo: e.target.value.toUpperCase() })}
                                        placeholder="Ej: FIS-101"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Horas</label>
                                    <input
                                        type="number"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none"
                                        value={newAsigData.horas}
                                        onChange={e => setNewAsigData({ ...newAsigData, horas: parseInt(e.target.value) })}
                                        min="1"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Carrera</label>
                                    <select
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none"
                                        value={newAsigData.id_carrera}
                                        onChange={e => setNewAsigData({ ...newAsigData, id_carrera: e.target.value })}
                                    >
                                        <option value="">Seleccionar...</option>
                                        {carreras.filter(c => [8, 9, 10].includes(c.id_carrera)).map(c => (
                                            <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Mencion</label>
                                    <select
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none"
                                        value={newAsigData.id_mencion}
                                        onChange={e => setNewAsigData({ ...newAsigData, id_mencion: e.target.value })}
                                        disabled={!newAsigData.id_carrera}
                                    >
                                        <option value="">Seleccionar...</option>
                                        {menciones.filter(m => m.id_carrera == newAsigData.id_carrera).map(m => (
                                            <option key={m.id_mencion} value={m.id_mencion}>{m.nombre}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Nivel</label>
                                    <select
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none"
                                        value={newAsigData.nivel}
                                        onChange={e => setNewAsigData({ ...newAsigData, nivel: parseInt(e.target.value) })}
                                    >
                                        {niveles.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Prerequisito</label>
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 uppercase focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none"
                                        value={newAsigData.prerequisito_codigo}
                                        onChange={e => setNewAsigData({ ...newAsigData, prerequisito_codigo: e.target.value.toUpperCase() })}
                                        placeholder="Opcional"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => { setShowNewAsigModal(false); setEditingSubject(null); }}
                                    className="px-6 py-2 border rounded-full hover:bg-gray-50 text-gray-700 font-medium text-sm transition"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCreateAsignatura}
                                    className="px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 font-medium text-sm transition"
                                >
                                    {editingSubject ? 'Actualizar' : 'Crear Asignatura'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Asignar Docente (Estilos actualizados) */}
            {showModal && selectedAsignatura && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center flex-shrink-0">
                            <div>
                                <h3 className="text-xl font-bold text-blue-900">
                                    {formData.id_asignacion ? 'Editar Paralelo' : 'Agregar Paralelo'}
                                </h3>
                                <p className="text-sm text-gray-600">{selectedAsignatura.codigo} - {selectedAsignatura.nombre}</p>
                            </div>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>


                        <div className="p-6 overflow-y-auto flex-1 max-h-[65vh] scrollbar-thin">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-1">Docente *</label>
                                    <select
                                        value={formData.id_docente}
                                        onChange={e => setFormData({ ...formData, id_docente: e.target.value })}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none"
                                    >
                                        <option value="">Seleccionar docente...</option>
                                        {docentes.map(d => (
                                            <option key={d.id_docente} value={d.id_docente}>
                                                {d.nombres} {d.apellidos}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Paralelo</label>
                                    <input
                                        type="text"
                                        value={formData.paralelo}
                                        onChange={e => {
                                            const val = e.target.value.toUpperCase().slice(0, 2);
                                            setFormData({ ...formData, paralelo: val });
                                        }}
                                        className="w-full px-3 py-2 border-2 rounded-md font-mono text-center text-lg"
                                        placeholder="A1"
                                        maxLength={2}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Capacidad</label>
                                    <input
                                        type="number"
                                        value={formData.capacidad}
                                        onChange={e => setFormData({ ...formData, capacidad: parseInt(e.target.value) })}
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none"
                                        min="1"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Color</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={formData.color}
                                            onChange={e => setFormData({ ...formData, color: e.target.value })}
                                            className="w-10 h-10 rounded border cursor-pointer"
                                        />
                                        <span className="text-xs text-gray-400">{formData.color}</span>
                                    </div>
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium mb-2">Días y Horarios por Día *</label>
                                    <div className="space-y-3">
                                        {diasSemana.map(dia => {
                                            const isActive = formData.dias.includes(dia);
                                            const detalle = formData.horarios_detalle[dia] || {};
                                            return (
                                                <div key={dia} className={`border rounded-lg p-3 ${isActive ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
                                                    <label className="flex items-center gap-3 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={isActive}
                                                            onChange={() => toggleDia(dia)}
                                                            className="w-5 h-5 rounded border-gray-300 text-blue-600"
                                                        />
                                                        <span className="font-medium text-sm">{dia}</span>
                                                    </label>
                                                    {isActive && (
                                                        <div className="grid grid-cols-3 gap-3 mt-3 pl-8">
                                                            <div>
                                                                <label className="block text-xs font-medium mb-1 text-gray-600">Hora Inicio</label>
                                                                <input type="time" value={detalle.hora_inicio || ''}
                                                                    onChange={(e) => handleDetalleChange(dia, 'hora_inicio', e.target.value)}
                                                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm" />

                                                            </div>
                                                            <div>
                                                                <label className="block text-xs font-medium mb-1 text-gray-600">Hora Fin</label>
                                                                <input
                                                                    type="time"
                                                                    value={detalle.hora_fin || ''}
                                                                    onChange={(e) => handleDetalleChange(dia, 'hora_fin', e.target.value)}
                                                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs font-medium mb-1 text-gray-600">Sala</label>
                                                                <select
                                                                    value={detalle.sala || ''}
                                                                    onChange={(e) => handleDetalleChange(dia, 'sala', e.target.value)}
                                                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                                                                >
                                                                    <option value="">Sala...</option>
                                                                    {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                                                                        <option key={n} value={n}>{n}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-100">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="px-6 py-2 border rounded-full hover:bg-gray-50 text-gray-700 font-medium text-sm transition"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleGuardarAsignacion}
                                    className="px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 font-medium text-sm transition shadow-sm"
                                >
                                    Guardar Asignación
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Calendario (Nuevo) */}
            {showCalendarModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50 flex-wrap gap-4">
                            <div>
                                <h2 className="text-xl font-bold text-blue-900">
                                    Horario General - Gestión {filtroGestion}
                                </h2>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                {/* Filtro por Sala */}
                                <select
                                    value={filtroSala}
                                    onChange={(e) => setFiltroSala(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todas las Salas</option>
                                    {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                                        <option key={num} value={num.toString()}>{`Sala ${num}`}</option>
                                    ))}
                                </select>

                                {/* Filtro por Docente */}
                                <select
                                    value={filtroDocente}
                                    onChange={(e) => setFiltroDocente(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todos los Docentes</option>
                                    {docentes.map(doc => (
                                        <option key={doc.id_docente} value={doc.id_docente}>
                                            {doc.nombres} {doc.apellidos}
                                        </option>
                                    ))}
                                </select>

                                {/* Filtro por Día */}
                                <select
                                    value={filtroDia}
                                    onChange={(e) => setFiltroDia(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-full text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Todos los Días</option>
                                    {diasSemana.map(dia => (
                                        <option key={dia} value={dia}>{dia}</option>
                                    ))}
                                </select>

                                <button
                                    onClick={() => {
                                        const exportFiltrados = asignaciones.filter(a => {
                                            const estadoDir = (a.estado_direccion || '').toLowerCase();
                                            if (estadoDir === 'rechazado' || estadoDir === 'pendiente') return false;

                                            if (filtroSala) {
                                                if (a.horarios_detalle && Array.isArray(a.horarios_detalle) && a.horarios_detalle.length > 0) {
                                                    if (!a.horarios_detalle.some(d => d.sala?.toString() === filtroSala.toString())) return false;
                                                } else {
                                                    if (a.sala?.toString() !== filtroSala.toString()) return false;
                                                }
                                            }
                                            if (filtroDocente && String(a.id_docente) !== String(filtroDocente)) return false;
                                            if (filtroDia) {
                                                if (a.horarios_detalle && Array.isArray(a.horarios_detalle) && a.horarios_detalle.length > 0) {
                                                    if (!a.horarios_detalle.some(d => d.dia === filtroDia)) return false;
                                                } else {
                                                    if (!a.dias || !a.dias.includes(filtroDia)) return false;
                                                }
                                            }
                                            return true;
                                        });

                                        try {
                                            const data = exportFiltrados.filter(a => (a.hora_inicio && a.hora_fin) || (a.horarios_detalle && a.horarios_detalle.length > 0)).map((a, idx) => {
                                                const asigInfo = asignaturas.find(as => parseInt(as.id_asignatura) === parseInt(a.id_asignatura));
                                                return {
                                                    'No': idx + 1,
                                                    'Código': asigInfo?.codigo || '-',
                                                    'Asignatura': asigInfo?.nombre || '-',
                                                    'Docente': getDocenteNombre(a.id_docente),
                                                    'Sala': a.sala || '-',
                                                    'Paralelo': a.paralelo || '-',
                                                    'Hora Inicio': a.hora_inicio || (a.horarios_detalle?.[0]?.hora_inicio || '-'),
                                                    'Hora Fin': a.hora_fin || (a.horarios_detalle?.[0]?.hora_fin || '-'),
                                                    'Días': a.dias?.join(', ') || a.horarios_detalle?.map(d => d.dia).join(', ') || '-'
                                                };
                                            });

                                            if (data.length === 0) {
                                                alert('No hay datos para exportar');
                                                return;
                                            }

                                            const ws = XLSX.utils.json_to_sheet(data);
                                            const wb = XLSX.utils.book_new();
                                            XLSX.utils.book_append_sheet(wb, ws, 'Horario');
                                            XLSX.writeFile(wb, `Horario_General_${filtroGestion}.xlsx`);
                                        } catch (error) {
                                            console.error('Error al exportar Horario:', error);
                                            alert('Error al generar el archivo Excel.');
                                        }
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 text-sm font-medium transition"
                                >
                                    <Download size={16} />
                                </button>
                                <button onClick={() => setShowCalendarModal(false)} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full transition">
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-grow overflow-auto p-4 bg-gray-100">
                            {(() => {
                                const paletaColores = [
                                    'bg-blue-600 hover:bg-blue-700',
                                    'bg-indigo-600 hover:bg-indigo-700',
                                    'bg-violet-600 hover:bg-violet-700',
                                    'bg-purple-600 hover:bg-purple-700',
                                    'bg-emerald-600 hover:bg-emerald-700',
                                    'bg-teal-600 hover:bg-teal-700',
                                    'bg-cyan-600 hover:bg-cyan-700',
                                    'bg-sky-600 hover:bg-sky-700',
                                    'bg-amber-600 hover:bg-amber-700',
                                    'bg-rose-600 hover:bg-rose-700'
                                ];

                                const obtenerColorAsignatura = (idAsignatura) => {
                                    if (!idAsignatura) return paletaColores[0];
                                    const index = parseInt(idAsignatura) % paletaColores.length;
                                    return paletaColores[index];
                                };

                                // FILTRO PRINCIPAL: Excluye rechazados, pendientes y aplica los selectores
                                const asignacionesFiltradas = asignaciones.filter(a => {
                                    const estadoDir = (a.estado_direccion || '').toLowerCase();
                                    if (estadoDir === 'rechazado' || estadoDir === 'pendiente') return false;

                                    if (filtroSala) {
                                        if (a.horarios_detalle && Array.isArray(a.horarios_detalle) && a.horarios_detalle.length > 0) {
                                            if (!a.horarios_detalle.some(d => d.sala?.toString() === filtroSala.toString())) return false;
                                        } else {
                                            if (a.sala?.toString() !== filtroSala.toString()) return false;
                                        }
                                    }
                                    if (filtroDocente && String(a.id_docente) !== String(filtroDocente)) return false;
                                    if (filtroDia) {
                                        if (a.horarios_detalle && Array.isArray(a.horarios_detalle) && a.horarios_detalle.length > 0) {
                                            if (!a.horarios_detalle.some(d => d.dia === filtroDia)) return false;
                                        } else {
                                            if (!a.dias || !a.dias.includes(filtroDia)) return false;
                                        }
                                    }
                                    return true;
                                });

                                const horariosAplanados = [];
                                asignacionesFiltradas.forEach(a => {
                                    const colorAsignado = obtenerColorAsignatura(a.id_asignatura);

                                    if (a.horarios_detalle && Array.isArray(a.horarios_detalle) && a.horarios_detalle.length > 0) {
                                        a.horarios_detalle.forEach(detalle => {
                                            if (filtroDia && detalle.dia !== filtroDia) return;
                                            if (filtroSala && detalle.sala?.toString() !== filtroSala.toString()) return;
                                            if (!detalle.hora_inicio || !detalle.hora_fin || !detalle.dia) return;

                                            horariosAplanados.push({
                                                ...a,
                                                dia: detalle.dia.toLowerCase(),
                                                hora_inicio: detalle.hora_inicio,
                                                hora_fin: detalle.hora_fin,
                                                sala: detalle.sala,
                                                _color: colorAsignado
                                            });
                                        });
                                    } else if (a.dias && Array.isArray(a.dias) && a.hora_inicio && a.hora_fin) {
                                        a.dias.forEach(d => {
                                            if (filtroDia && d !== filtroDia) return;
                                            if (filtroSala && a.sala?.toString() !== filtroSala.toString()) return;

                                            horariosAplanados.push({
                                                ...a,
                                                dia: d.toLowerCase(),
                                                _color: colorAsignado
                                            });
                                        });
                                    }
                                });

                                const diasTabla = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes'];
                                const slotMinutes = 30;
                                const startHour = 6, endHour = 18;
                                const totalSlots = ((endHour - startHour) * 60) / slotMinutes;

                                const toMin = (hhmm) => {
                                    const [h, m] = (hhmm || '').substring(0, 5).split(':').map(Number);
                                    return h * 60 + m;
                                };

                                const dayData = diasTabla.map(dia => {
                                    const entries = horariosAplanados
                                        .filter(h => h.dia === dia && h.hora_inicio && h.hora_fin)
                                        .map(h => ({
                                            ...h,
                                            startMin: toMin(h.hora_inicio),
                                            endMin: toMin(h.hora_fin),
                                        }))
                                        .sort((a, b) => a.startMin - b.startMin);

                                    const laneEnds = [];
                                    entries.forEach(e => {
                                        let laneIdx = laneEnds.findIndex(end => end <= e.startMin);
                                        if (laneIdx === -1) {
                                            laneIdx = laneEnds.length;
                                            laneEnds.push(e.endMin);
                                        } else {
                                            laneEnds[laneIdx] = e.endMin;
                                        }
                                        e.lane = laneIdx;
                                    });

                                    const maxLanes = Math.max(1, laneEnds.length);
                                    return { dia, entries, maxLanes };
                                });

                                const totalLanes = dayData.reduce((sum, d) => sum + d.maxLanes, 0);
                                let acumulado = 0;
                                const dayOffsets = dayData.map(d => {
                                    const offset = acumulado;
                                    acumulado += d.maxLanes;
                                    return offset;
                                });

                                const rowHeight = 42;
                                const gridTemplateColumns = `110px repeat(${totalLanes}, minmax(140px, 1fr))`;

                                return (
                                    <div className="bg-white rounded-lg shadow overflow-auto" style={{ maxHeight: '75vh' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns, position: 'relative' }}>
                                            <div className="border bg-gray-100 font-semibold text-center py-2 sticky top-0 z-20"
                                                style={{ gridColumn: '1', gridRow: '1' }}>
                                                Intervalo
                                            </div>

                                            {dayData.map((d, di) => (
                                                <div key={d.dia}
                                                    className="border bg-gray-100 font-bold text-center py-2 sticky top-0 z-20 uppercase"
                                                    style={{ gridColumn: `${dayOffsets[di] + 2} / span ${d.maxLanes}`, gridRow: '1' }}>
                                                    {d.dia}
                                                </div>
                                            ))}

                                            {Array.from({ length: totalSlots }).map((_, i) => {
                                                const mins = startHour * 60 + i * slotMinutes;
                                                const h = Math.floor(mins / 60), m = mins % 60;
                                                const mins2 = mins + slotMinutes;
                                                const hEnd = Math.floor(mins2 / 60), mEnd = mins2 % 60;
                                                const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')} - ${String(hEnd).padStart(2, '0')}:${String(mEnd).padStart(2, '0')}`;
                                                return (
                                                    <div key={i} className="border bg-gray-50 text-xs flex items-center justify-center px-1 font-mono text-gray-500"
                                                        style={{ gridColumn: '1', gridRow: i + 2, height: rowHeight }}>
                                                        {label}
                                                    </div>
                                                );
                                            })}

                                            {dayData.map((d, di) => (
                                                Array.from({ length: totalSlots }).map((_, i) => (
                                                    Array.from({ length: d.maxLanes }).map((_, li) => (
                                                        <div key={`bg-${di}-${i}-${li}`} className="border bg-white"
                                                            style={{ gridColumn: dayOffsets[di] + 2 + li, gridRow: i + 2, height: rowHeight }} />
                                                    ))
                                                ))
                                            ))}

                                            {dayData.map((d, di) => {
                                                return d.entries.map((entry, idx) => {
                                                    const startSlotIndex = Math.floor((entry.startMin - (startHour * 60)) / slotMinutes);
                                                    const durationMinutes = entry.endMin - entry.startMin;
                                                    const spanRows = Math.max(1, Math.ceil(durationMinutes / slotMinutes));
                                                    const gridRowStart = startSlotIndex + 2;
                                                    const gridCol = dayOffsets[di] + 2 + entry.lane;

                                                    const asignaturaInfo = asignaturas.find(as => parseInt(as.id_asignatura) === parseInt(entry.id_asignatura));
                                                    const nombreAsig = asignaturaInfo ? asignaturaInfo.nombre : 'Desconocido';
                                                    const codigoAsig = asignaturaInfo ? asignaturaInfo.codigo : '???';
                                                    const docenteNombre = getDocenteNombre(entry.id_docente);

                                                    return (
                                                        <div
                                                            key={`event-${di}-${idx}`}
                                                            className={`p-2 text-white text-xs shadow-md flex flex-col justify-between overflow-hidden rounded transition-all ${entry._color}`}
                                                            style={{
                                                                gridColumn: gridCol,
                                                                gridRow: `${gridRowStart} / span ${spanRows}`,
                                                                margin: '1px',
                                                                zIndex: 10
                                                            }}
                                                            title={`${codigoAsig} - ${nombreAsig}\nDocente: ${docenteNombre}\nSala: ${entry.sala || '-'}\nParalelo: ${entry.paralelo || '-'}\nHorario: ${entry.hora_inicio?.substring(0, 5)} - ${entry.hora_fin?.substring(0, 5)}`}
                                                        >
                                                            <div>
                                                                <div className="font-bold truncate">{codigoAsig} {entry.paralelo ? `(${entry.paralelo})` : ''}</div>
                                                                <div className="truncate opacity-90 text-[11px]">{nombreAsig}</div>
                                                            </div>
                                                            <div className="flex justify-between text-[10px] opacity-85 mt-1 border-t border-white/20 pt-0.5">
                                                                <span>Sala: {entry.sala || '?'}</span>
                                                                <span className="truncate max-w-[80px]">{docenteNombre}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                });
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Confirmación Solicitar Aprobación */}
            {showApprovalModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <div className="flex justify-between items-center border-b sticky top-0 bg-white z-10 mb-6">
                                <h2 className="text-xl font-bold text-blue-900">Solicitar Aprobación</h2>
                                <button onClick={() => setShowApprovalModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <X size={24} />
                                </button>
                            </div>
                            <p className="text-gray-600 mb-6">
                                ¿Desea solicitar la aprobación de los horarios de la gestión <strong>{filtroGestion}</strong> a Dirección?
                            </p>
                            <p className="text-sm text-gray-500 mb-6">
                                Una vez enviada, Dirección revisará y aprobará o rechazará todos los horarios de esta gestión.
                            </p>
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setShowApprovalModal(false)}
                                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={async () => {
                                        try {
                                            const response = await fetch(`${API_URL}/jefatura/solicitar-aprobacion-gestion`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ gestion: filtroGestion })
                                            });
                                            const data = await response.json();
                                            if (response.ok) {
                                                alert(data.message);
                                                setShowApprovalModal(false);
                                                loadData(); // Reload data to reflect updated estado_direccion
                                            } else {
                                                alert(data.error || 'Error al solicitar aprobación');
                                            }
                                        } catch (error) {
                                            console.error('Error:', error);
                                            alert('Error al solicitar aprobación');
                                        }
                                    }}
                                    className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                                >
                                    Aceptar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Observaciones (Compartido CPA/PAA/Licenciatura) */}
            {selectedObservation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-xl font-bold flex items-center gap-2 text-orange-600">
                                <MessageSquare size={20} />
                                Observaciones de Dirección
                            </h2>
                            <button onClick={() => setSelectedObservation(null)} className="text-gray-400 hover:text-gray-600 text-2xl">
                                <X size={28} />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="bg-orange-50 text-orange-900 p-4 rounded-lg text-sm mb-6 border border-orange-100 max-h-[400px] overflow-y-auto whitespace-pre-wrap">
                                {selectedObservation}
                            </div>
                            <div className="flex justify-end">
                                <button
                                    onClick={() => setSelectedObservation(null)}
                                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded-full hover:bg-gray-300 transition text-sm font-medium"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}